import { useMemo } from "react";
import i18n from "@core/i18n";
import type { SettingKey } from "./types";

export type HintTone = "amber" | "amber-soft" | "info";

export interface Hint {
  /** Setting auquel le hint est rattaché */
  key: SettingKey;
  /** Texte d'explication court */
  message: string;
  /** Valeur prête à appliquer si le user click "Utiliser « X » →". `undefined`
   *  si pas d'action one-click (juste un nudge). */
  applyValue?: unknown;
  /** Étiquette humaine de la valeur à proposer (ex `"Salle de réunion"`).
   *  Utilisée pour formater le lien clickable. */
  suggestionLabel?: string;
  /** Tone visuel — affecte le bandeau (amber chaud, amber-soft, info bleu). */
  tone: HintTone;
}

export type HintSeverity = "none" | "suggestions" | "warnings";

export interface HintsResult {
  /** Tous les hints actifs */
  hints: Hint[];
  /** Index par setting key (Hint ou null) */
  byKey: Partial<Record<SettingKey, Hint>>;
  /** Sévérité globale, pour décider le badge du groupe Suggestions */
  severity: HintSeverity;
}

/**
 * Entrée minimaliste pour calculer les hints. Ne dépend PAS de DraftSummary
 * pour rester simple à tester (juste les valeurs du draft).
 */
export interface HintsInput {
  title: string;
  location: string | null | undefined;
  expiresAt: string | null | undefined;
  audienceMode: string;
  resultsVisibility: string;
  sendNotifications: boolean;
  maxResponses: number | null | undefined;
  participantEmailsCount: number;
  /** Première date sélectionnée — sert à proposer "expire 1 semaine avant" */
  firstSelectedDate?: string | null;
}

/** Détection robuste de mots-clés "réunion-team" dans le titre. */
function detectTeamKeyword(title: string): string | null {
  const lower = (title || "").toLowerCase();
  if (!lower.trim()) return null;
  // Mots déclencheurs avec capture pour réinjection dans le message
  const patterns: Array<[RegExp, string]> = [
    [/\béquipe\b/, "équipe"],
    [/\bréunion\b/, "réunion"],
    [/\bmeeting\b/, "meeting"],
    [/\bteam\b/, "team"],
    [/\bstandup\b/, "standup"],
    [/\bworkshop\b/, "workshop"],
    [/\batelier\b/, "atelier"],
  ];
  for (const [re, kw] of patterns) {
    if (re.test(lower)) return kw;
  }
  return null;
}

function formatDateShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(i18n.language || "fr", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(d);
}

/** Soustrait N jours à une date ISO et retourne ISO YYYY-MM-DD (UTC midnight). */
function subtractDays(iso: string, days: number): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

/**
 * Calcule les hints contextuels actifs pour le `PrePublishModal`. Cf. plan
 * "Hints — règles centralisées".
 */
export function computeSettingsHints(input: HintsInput): HintsResult {
  const hints: Hint[] = [];

  // 1. Lieu vide
  const locationEmpty =
    !input.location ||
    (typeof input.location === "string" && input.location.trim() === "");
  if (locationEmpty) {
    const keyword = detectTeamKeyword(input.title);
    if (keyword) {
      hints.push({
        key: "location",
        message: i18n.t("settings_hints.location_team_keyword", { keyword }),
        applyValue: i18n.t("settings_hints.location_suggestion_meeting_room"),
        suggestionLabel: i18n.t(
          "settings_hints.location_suggestion_meeting_room",
        ),
        tone: "amber",
      });
    } else {
      hints.push({
        key: "location",
        message: i18n.t("settings_hints.location_empty"),
        tone: "amber-soft",
      });
    }
  }

  // 2. Date d'expiration null
  if (!input.expiresAt) {
    if (input.firstSelectedDate) {
      const suggestedIso = subtractDays(input.firstSelectedDate, 7);
      hints.push({
        key: "expiresAt",
        message: i18n.t("settings_hints.expires_recommended", {
          date: formatDateShort(input.firstSelectedDate),
        }),
        applyValue: suggestedIso,
        suggestionLabel: formatDateShort(suggestedIso),
        tone: "amber",
      });
    } else {
      hints.push({
        key: "expiresAt",
        message: i18n.t("settings_hints.expires_none"),
        tone: "amber-soft",
      });
    }
  }

  // 3. Notifications désactivées en mode emails
  if (!input.sendNotifications && input.audienceMode === "emails") {
    hints.push({
      key: "sendNotifications",
      message: i18n.t("settings_hints.notifications_off_emails"),
      applyValue: true,
      tone: "amber",
    });
  }

  // 4. Visibilité publique en mode emails (info)
  if (input.resultsVisibility === "public" && input.audienceMode === "emails") {
    hints.push({
      key: "resultsVisibility",
      message: i18n.t("settings_hints.visibility_public_emails"),
      tone: "info",
    });
  }

  // 5. Mode lien public alors que des emails ont été ajoutés
  if (input.audienceMode === "link" && input.participantEmailsCount > 0) {
    hints.push({
      key: "audienceMode",
      message: i18n.t("settings_hints.link_mode_with_emails"),
      applyValue: "emails",
      suggestionLabel: i18n.t("settings_hints.link_mode_with_emails_label"),
      tone: "amber",
    });
  }

  // 6. maxResponses null en mode link
  if (input.maxResponses == null && input.audienceMode === "link") {
    hints.push({
      key: "maxResponses",
      message: i18n.t("settings_hints.max_responses_link"),
      tone: "amber-soft",
    });
  }

  const byKey: Partial<Record<SettingKey, Hint>> = {};
  for (const h of hints) byKey[h.key] = h;

  let severity: HintSeverity = "none";
  if (hints.length > 0) {
    severity = hints.some((h) => h.applyValue !== undefined)
      ? "warnings"
      : "suggestions";
  }

  return { hints, byKey, severity };
}

/** Hook React mémoïsé par les inputs */
export function useSettingsHints(input: HintsInput): HintsResult {
  return useMemo(
    () => computeSettingsHints(input),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      input.title,
      input.location,
      input.expiresAt,
      input.audienceMode,
      input.resultsVisibility,
      input.sendNotifications,
      input.maxResponses,
      input.participantEmailsCount,
      input.firstSelectedDate,
    ],
  );
}
